import React, { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/InputDefault';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/DefaultSelectDropdown';
import { Switch } from '@/components/ui/switch';
import { DATA_SOURCES, DataSourceType, getColumnsForDataSource, TableColumn } from '@/lib/dataSources';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getSelectedGame } from '@/lib/stores/unifiedUserStore';

import { usePlatformTranslation } from '@/hooks/usePlatformTranslation';
interface DataSourceOrganizerProps {
  dataSource: DataSourceType | undefined;
  selectedColumns: string[];
  columnLabels: Record<string, string>;
  onDataSourceChange: (dataSource: DataSourceType) => void;
  onSelectedColumnsChange: (columns: string[]) => void;
  onColumnLabelsChange: (labels: Record<string, string>) => void;
}

// Sortable column item component
const SortableColumnItem: React.FC<{
  column: TableColumn;
  isSelected: boolean;
  customLabel: string;
  onToggle: (columnKey: string, isSelected: boolean) => void;
  onLabelChange: (columnKey: string, newLabel: string) => void;
}> = ({ column, isSelected, customLabel, onToggle, onLabelChange }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 border rounded-sm transition-colors ${
        isSelected
          ? "bg-white border-blue-200 shadow-sm"
          : "bg-gray-100 border-gray-200"
      } ${isDragging ? "shadow-lg" : ""}`}
    >
      <div className="flex items-center justify-center">
        <Switch
          size="sm"
          checked={isSelected}
          onCheckedChange={(checked) => onToggle(column.key, checked)}
        />
      </div>

      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex items-center gap-2">
          <Input
            value={customLabel}
            onChange={(e) => onLabelChange(column.key, e.target.value)}
            className={`text-sm font-medium border-0 p-0 h-auto bg-gray-50 border-gray-200 border rounded-sm ${
              isSelected ? "text-gray-900" : "text-gray-600"
            }`}
            placeholder={column.label}
          />
          {customLabel !== column.label && (
            <span className="text-xs text-blue-600 bg-blue-100 px-1 rounded-sm">
              Custom
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 font-mono">
          {column.key}
        </div>
      </div>

      {isSelected && (
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-2 text-gray-400 hover:text-gray-600 transition-colors"
          title={t('platform.ui.datasourceorganizer.drag_to_reorder')}
        >
          <svg
            width="16"
            height="16"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 8h16M4 16h16"
            />
          </svg>
        </div>
      )}
    </div>
  );
};

const DataSourceOrganizer: React.FC<DataSourceOrganizerProps> = ({
  dataSource,
  selectedColumns,
  columnLabels,
  onDataSourceChange,
  onSelectedColumnsChange,
  onColumnLabelsChange,
}) => {

  const selectedGame = getSelectedGame();
  const defaultLanguage = selectedGame?.settings?.defaultLanguage || "en";
  
  const availableColumns = useMemo(() => {
    return dataSource ? getColumnsForDataSource(dataSource, defaultLanguage as string) : [];
  }, [dataSource, defaultLanguage]);
  
  const [previousDataSource, setPreviousDataSource] = React.useState<DataSourceType | undefined>(undefined);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle data source changes and column selection
  React.useEffect(() => {
    if (availableColumns.length > 0) {
      // If data source changed or this is the initial load, update selected columns
      if (dataSource !== previousDataSource || previousDataSource === undefined) {
        const allColumnKeys = availableColumns.map(col => col.key);
        onSelectedColumnsChange(allColumnKeys);
        setPreviousDataSource(dataSource);
      } else if (selectedColumns.length === 0) {
        // If no columns are selected, select all by default
        const defaultSelected = availableColumns.map(col => col.key);
        onSelectedColumnsChange(defaultSelected);
      }
    }
  }, [dataSource, availableColumns, selectedColumns.length, onSelectedColumnsChange, previousDataSource]);

  const handleColumnToggle = (columnKey: string, isSelected: boolean) => {
  const { t } = usePlatformTranslation();
    let newSelectedColumns: string[];
    if (isSelected) {
      newSelectedColumns = [...selectedColumns, columnKey];
    } else {
      newSelectedColumns = selectedColumns.filter(key => key !== columnKey);
    }
    onSelectedColumnsChange(newSelectedColumns);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      // Filter selected columns to only include those that exist in current data source
      const validSelectedColumns = selectedColumns.filter(key => availableColumns.some(col => col.key === key));
      const oldIndex = validSelectedColumns.indexOf(active.id as string);
      const newIndex = validSelectedColumns.indexOf(over?.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newValidSelectedColumns = arrayMove(validSelectedColumns, oldIndex, newIndex);
        // Keep any columns that don't exist in current data source at the end
        const invalidColumns = selectedColumns.filter(key => !availableColumns.some(col => col.key === key));
        const newSelectedColumns = [...newValidSelectedColumns, ...invalidColumns];
        onSelectedColumnsChange(newSelectedColumns);
      }
    }
  };

  const handleColumnLabelChange = (columnKey: string, newLabel: string) => {
    const newColumnLabels = { ...columnLabels, [columnKey]: newLabel };
    onColumnLabelsChange(newColumnLabels);
  };

  const handleDataSourceChange = (value: string) => {
    console.log('DataSourceOrganizer handleDataSourceChange called with:', value);
    const newDataSource = value as DataSourceType;
    onDataSourceChange(newDataSource);
    
    // Reset column labels when data source changes
    console.log('DataSourceOrganizer resetting column labels');
    onColumnLabelsChange({});
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="dataSource">{t('platform.ui.datasourceorganizer.data_source')}</Label>
        <Select
          value={dataSource || ''}
          onValueChange={handleDataSourceChange}
        >
          <SelectTrigger>
            <SelectValue placeholder={t('platform.ui.datasourceorganizer.select_data_source')} />
          </SelectTrigger>
          <SelectContent>
            {Object.values(DATA_SOURCES).map((source) => (
              <SelectItem key={source.type} value={source.type}>
                {source.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">
          {dataSource ? DATA_SOURCES[dataSource]?.description : 'Select a data source to display dynamic data'}
        </span>
      </div>

      {/* Column Selection */}
      {dataSource && availableColumns.length > 0 && (
        <div className="space-y-3">
          <Label>{t('platform.ui.datasourceorganizer.column_selection')}</Label>
          <div className="text-xs text-muted-foreground mb-2">
            Select which columns to display, drag to reorder them, and customize their labels
          </div>
          
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={selectedColumns.filter(key => availableColumns.some(col => col.key === key))}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2 max-h-60 overflow-y-auto border rounded-sm p-3 bg-gray-50">
                {availableColumns
                  .sort((a, b) => {
                    const aIndex = selectedColumns.indexOf(a.key);
                    const bIndex = selectedColumns.indexOf(b.key);
                    
                    // If both are selected, sort by their order in selectedColumns
                    if (aIndex !== -1 && bIndex !== -1) {
                      return aIndex - bIndex;
                    }
                    
                    // If only one is selected, put selected items first
                    if (aIndex !== -1) return -1;
                    if (bIndex !== -1) return 1;
                    
                    // If neither is selected, maintain original order
                    return 0;
                  })
                  .map((column) => {
                    const isSelected = selectedColumns.includes(column.key);
                    const customLabel = columnLabels[column.key] || column.label;
                    
                    return (
                      <SortableColumnItem
                        key={column.key}
                        column={column}
                        isSelected={isSelected}
                        customLabel={customLabel}
                        onToggle={handleColumnToggle}
                        onLabelChange={handleColumnLabelChange}
                      />
                    );
                  })}
              </div>
            </SortableContext>
          </DndContext>

          {selectedColumns.length > 0 && (
            <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded-sm">
              <span className="font-medium">{t('platform.ui.datasourceorganizer.selected_columns')}</span>{" "}
              {selectedColumns.filter(key => availableColumns.some(col => col.key === key)).length} of {availableColumns.length}
              {selectedColumns.length > 0 && (
                <div className="mt-1 text-gray-600">
                  Order:{" "}
                  {selectedColumns
                    .filter(key => availableColumns.some(col => col.key === key))
                    .map(
                      (key) => {
                        const column = availableColumns.find((col) => col.key === key);
                        const customLabel = columnLabels[key];
                        return customLabel || column?.label;
                      }
                    )
                    .join(" → ")}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DataSourceOrganizer; 